/**
 * CustomerTableRow Component
 * Individual row component for customer table
 */

import React from 'react';
import { TableRow, TableCell, Avatar, Typography, Box, IconButton, Tooltip } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import type { Customer } from '../../../types/customer';
import { getInitials } from '../../../utils/formatters';
import { STYLES } from '../constants/customerTableConfig';

interface CustomerTableRowProps {
  customer: Customer;
  onRowClick: (customerId: string) => void;
  onDeleteClick: (event: React.MouseEvent, customer: Customer) => void;
}

export const CustomerTableRow: React.FC<CustomerTableRowProps> = ({
  customer,
  onRowClick,
  onDeleteClick,
}) => {
  return (
    <Tooltip
      title={customer.notes || 'No notes'}
      placement="top"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            fontSize: '0.875rem',
            padding: '8px 12px',
          },
        },
        arrow: {
          sx: {
            color: 'rgba(0, 0, 0, 0.9)',
          },
        },
      }}
    >
      <TableRow onClick={() => onRowClick(customer.id)} sx={STYLES.tableRow}>
        {/* Customer Name with Avatar */}
        <TableCell sx={STYLES.tableCellBody}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={STYLES.avatar}>{getInitials(customer.name)}</Avatar>
            <Box>
              <Typography variant="body2" sx={STYLES.customerName}>
                {customer.name}
              </Typography>
              <Typography variant="caption" sx={STYLES.customerEmail}>
                {customer.email || 'No email'}
              </Typography>
              {customer.address && (
                <Typography variant="caption" sx={STYLES.customerAddress}>
                  {customer.address}
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>

      {/* Trucks */}
      <TableCell sx={{ ...STYLES.tableCellBody, width: '60px' }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '1.125rem',
            color: '#374151',
          }}
        >
          {customer.truckCount}
        </Typography>
      </TableCell>

      {/* 2 Weeks - RED */}
      <TableCell sx={{ ...STYLES.tableCellBody, width: '100px' }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '1.125rem',
            color: customer.trucksDueWithin2Weeks > 0 ? '#dc2626' : '#6b7280',
            fontWeight: customer.trucksDueWithin2Weeks > 0 ? 600 : 400,
          }}
        >
          {customer.trucksDueWithin2Weeks}
        </Typography>
      </TableCell>

      {/* 2-6 Weeks - ORANGE */}
      <TableCell sx={{ ...STYLES.tableCellBody, width: '100px' }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '1.125rem',
            color: customer.trucksDueWithin2To6Weeks > 0 ? '#f97316' : '#6b7280',
            fontWeight: customer.trucksDueWithin2To6Weeks > 0 ? 600 : 400,
          }}
        >
          {customer.trucksDueWithin2To6Weeks}
        </Typography>
      </TableCell>

      {/* 6-12 Weeks - GREEN */}
      <TableCell sx={{ ...STYLES.tableCellBody, width: '120px' }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '1.125rem',
            color: customer.trucksDueWithin6To12Weeks > 0 ? '#16a34a' : '#6b7280',
            fontWeight: customer.trucksDueWithin6To12Weeks > 0 ? 600 : 400,
          }}
        >
          {customer.trucksDueWithin6To12Weeks}
        </Typography>
      </TableCell>

      {/* Actions */}
      <TableCell
        sx={{
          ...STYLES.tableCellBody,
          width: '40px',
          textAlign: 'center',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <IconButton size="small" onClick={(e) => onDeleteClick(e, customer)} sx={STYLES.deleteButton}>
            <DeleteIcon sx={{ fontSize: 26 }} />
          </IconButton>
        </Box>
      </TableCell>
      </TableRow>
    </Tooltip>
  );
};

export default CustomerTableRow;
